import { Group, Button, Text, Box, Image, TextInput } from '@mantine/core'
import { UseFormReturnType, useForm } from '@mantine/form'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { validatePassword } from 'utils/password'
import { Logo } from 'components/logo'
import { IconPassword } from '@tabler/icons-react'
import { useMediaQuery } from '@mantine/hooks'

interface FormValues {
  password: string
  confirmPassword: string
}

export const ChangePassword = () => {
  const { completeNewPassword } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const isSmallDevice = useMediaQuery('(max-width: 864px)')

  const form = useForm({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: {
      password: (value) => (validatePassword(value) ? null : 'The password must contain one uppercase, one lowercase, a number and at least 8 characters'),
      // eslint-disable-next-line quotes
      confirmPassword: (value, values) => (value === values?.password ? null : "Password doesn't match"),
    },
  })

  async function onSubmit(values: FormValues) {
    setIsLoading(true)
    await completeNewPassword(values.password)
    setIsLoading(false)
  }

  if (isSmallDevice) {
    return (
      <Box p={30} w="100%" display="flex" sx={{ flexDirection: 'column' }}>
        <Logo width={150} />
        <Box mt={120} mb={30} sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text align="center" size="40px">
            {t('authentication.change-password')}
          </Text>
        </Box>
        <ChangePasswordForm form={form} onSubmit={onSubmit} isLoading={isLoading} isSmall />
      </Box>
    )
  }

  return (
    <Box display="flex" w="100%">
      <Box
        w={400}
        bg="custom-primary-color.0"
        p={30}
        mx="auto"
        display="flex"
        pos="relative"
        sx={{
          borderTopLeftRadius: '4px',
          borderBottomLeftRadius: '4px',
          color: 'white',
          overflow: 'hidden',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ position: 'absolute', top: 5, left: '-100px', right: 0, bottom: 0, height: '100%', minWidth: '1264px' }}>
          <Image src="https://cdn.expovisio.com/full-banner.png" alt="additional-img" />
        </Box>
        <Logo />
        <Box w="100%" pt={150} display="flex" sx={{ flex: 1, alignItems: 'center', justifyContent: 'flex-end', flexFlow: 'column' }}>
          <Button
            variant="filled"
            color="dark.4"
            radius="md"
            onClick={() => navigate('/session/login')}
            sx={{
              fontSize: '18px',
              lineHeight: '22px',
              color: 'white',
              fontWeight: 400,
              width: '216px',
              height: '50px',
              borderColor: 'white',
            }}
          >
            {t('authentication.sign-in')}
          </Button>
        </Box>
      </Box>
      <Box p={30} display="flex" sx={{ flex: 1, justifyContent: 'center', flexFlow: 'column', zIndex: 100 }}>
        <Box mb={30} sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text align="center" size="xl">
            {t('authentication.change-password')}
          </Text>
        </Box>

        <Box mb={50} sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text align="center" size="md">
            {t('authentication.change-temp-password')}
          </Text>
        </Box>

        <ChangePasswordForm form={form} onSubmit={onSubmit} isLoading={isLoading} />
      </Box>
    </Box>
  )
}

interface ChangePasswordFormProps {
  form: UseFormReturnType<FormValues>
  onSubmit: (values: FormValues) => Promise<void>
  isLoading: boolean
  isSmall?: boolean
}

function ChangePasswordForm({ form, onSubmit, isLoading, isSmall }: ChangePasswordFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <Box display="flex" sx={{ width: '100%', justifyContent: 'center' }}>
      <form style={{ width: '80%' }} onSubmit={form.onSubmit(onSubmit)}>
        <Group position="center" mb={10}>
          <TextInput
            w="70%"
            size="md"
            type="password"
            icon={<IconPassword width={15} height={15} stroke="#cecece" />}
            required
            placeholder={t('authentication.password') || ''}
            {...form.getInputProps('password')}
          />
        </Group>

        <Group position="center" mb={10}>
          <TextInput
            w="70%"
            size="md"
            type="password"
            icon={<IconPassword width={15} height={15} stroke="#cecece" />}
            required
            placeholder={t('authentication.confirm-password') || ''}
            {...form.getInputProps('confirmPassword')}
          />
        </Group>

        <Group position="center" sx={{ marginTop: 30 }}>
          {isSmall && (
            <Button
              variant="default"
              color="dark.4"
              radius="md"
              mr={10}
              onClick={() => navigate('/session/login')}
              sx={{
                fontSize: '18px',
                lineHeight: '22px',
                fontWeight: 400,
                height: '50px',
                borderColor: 'white',
              }}
            >
              {t('authentication.sign-in')}
            </Button>
          )}

          <Button
            color="custom-primary-color.0"
            radius="md"
            sx={{
              fontSize: '18px',
              lineHeight: '22px',
              color: 'white',
              fontWeight: 400,
              height: '50px',
            }}
            type="submit"
            loading={isLoading}
          >
            <Text sx={{ fontSize: '18px', lineHeight: '22px', fontWeight: 400 }}>{t('authentication.change-password')}</Text>
          </Button>
        </Group>
      </form>
    </Box>
  )
}
